import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import AdminModal from '../../components/admin/AdminModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import FormField from '../../components/admin/FormField';
import Badge from '../../components/admin/Badge';

function GaugeBar({ value, max = 100, label, color = '#1aad9f', warn = 80, critical = 90 }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const barColor = pct >= critical ? '#ef4444' : pct >= warn ? '#f59e0b' : color;
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px' }}>
        <span style={{ color: 'var(--admin-text-secondary)' }}>{label}</span>
        <span style={{ color: barColor, fontWeight: 600 }}>{value != null ? `${pct.toFixed(0)}%` : 'N/A'}</span>
      </div>
      <div style={{ height: '6px', background: 'var(--admin-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

function ServerCard({ server, onCheckNow, onViewHistory }) {
  const h = server.health;
  const isOnline = h && h.is_reachable;
  const age = h ? Math.floor((Date.now() - new Date(h.created_at).getTime()) / 60000) : null;

  return (
    <div className="admin-card" style={{ padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>{server.name}</h3>
          <span style={{ fontSize: '12px', color: 'var(--admin-text-muted)' }}>{server.hostname}</span>
        </div>
        <Badge variant={isOnline ? 'active' : h ? 'suspended' : 'pending'}>
          {isOnline ? 'Online' : h ? 'Offline' : 'No Data'}
        </Badge>
      </div>

      {h && isOnline ? (
        <>
          <GaugeBar value={h.cpu_usage} label="CPU" />
          <GaugeBar value={h.ram_usage} label="RAM" />
          <GaugeBar value={h.disk_usage} label="Disk" />

          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--admin-text-muted)', marginTop: '8px', borderTop: '1px solid var(--admin-border)', paddingTop: '8px' }}>
            <span><i className="fa-solid fa-microchip" style={{ marginRight: '4px' }} />{h.ram_used_mb || '?'}MB / {h.ram_total_mb || '?'}MB</span>
            <span><i className="fa-solid fa-hard-drive" style={{ marginRight: '4px' }} />{h.disk_used_gb || '?'}GB / {h.disk_total_gb || '?'}GB</span>
          </div>
          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--admin-text-muted)', marginTop: '4px' }}>
            <span style={{ color: '#22c55e' }}><i className="fa-brands fa-docker" style={{ marginRight: '4px' }} />{h.docker_containers_running} running</span>
            {h.docker_containers_stopped > 0 && <span style={{ color: '#a1a1aa' }}>{h.docker_containers_stopped} stopped</span>}
            {h.docker_containers_crashed > 0 && <span style={{ color: '#ef4444' }}>{h.docker_containers_crashed} crashed</span>}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--admin-text-muted)', marginTop: '6px' }}>
            <i className="fa-solid fa-clock" style={{ marginRight: '4px' }} />{h.response_time_ms}ms
            {age != null && <span style={{ marginLeft: '8px' }}>{age < 60 ? `${age}m ago` : `${Math.floor(age/60)}h ago`}</span>}
          </div>
        </>
      ) : (
        <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--admin-text-muted)', fontSize: '13px' }}>
          {h ? 'Server unreachable' : 'No health data yet'}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button className="admin-btn admin-btn--sm admin-btn--secondary" style={{ flex: 1 }} onClick={() => onCheckNow(server.id)}>
          <i className="fa-solid fa-arrows-rotate" style={{ marginRight: '4px' }} />Check
        </button>
        <button className="admin-btn admin-btn--sm admin-btn--ghost" style={{ flex: 1 }} onClick={() => onViewHistory(server)}>
          <i className="fa-solid fa-chart-line" style={{ marginRight: '4px' }} />History
        </button>
      </div>
    </div>
  );
}

function AdminMonitoring({ apiFetch, toast }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [historyServer, setHistoryServer] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyHours, setHistoryHours] = useState(24);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [tab, setTab] = useState('overview'); // overview, alerts, history
  const [alertRules, setAlertRules] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [editingRule, setEditingRule] = useState(null);
  const [savingRule, setSavingRule] = useState(false);
  const [deleteRuleConfirm, setDeleteRuleConfirm] = useState(null);

  const loadServers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/monitoring/overview');
      if (res.ok) setServers(await res.json());
      else toast.error('Error', 'Failed to load monitoring data');
    } catch { toast.error('Error', 'Failed to load monitoring data'); }
    finally { setLoading(false); }
  }, [apiFetch, toast]);

  const loadAlertRules = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/monitoring/alerts/rules');
      if (res.ok) setAlertRules(await res.json());
    } catch {}
  }, [apiFetch]);

  const loadAlertHistory = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/monitoring/alerts/history?limit=50');
      if (res.ok) setAlertHistory(await res.json());
    } catch {}
  }, [apiFetch]);

  useEffect(() => { loadServers(); loadAlertRules(); loadAlertHistory(); }, [loadServers, loadAlertRules, loadAlertHistory]);

  const handleCheckNow = async (serverId) => {
    setChecking(true);
    try {
      const body = serverId ? { server_id: serverId } : {};
      const res = await apiFetch('/api/admin/monitoring/check-now', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Done', 'Health check completed');
        await loadServers();
      } else {
        const data = await res.json();
        toast.error('Error', data.error || 'Health check failed');
      }
    } catch { toast.error('Error', 'Health check failed'); }
    finally { setChecking(false); }
  };

  const handleViewHistory = async (server) => {
    setHistoryServer(server);
    setHistoryLoading(true);
    try {
      const res = await apiFetch(`/api/admin/monitoring/servers/${server.id}/history?hours=${historyHours}`);
      if (res.ok) setHistory(await res.json());
    } catch {}
    finally { setHistoryLoading(false); }
  };

  const handleSaveRule = async () => {
    setSavingRule(true);
    try {
      if (editingRule.id) {
        const res = await apiFetch(`/api/admin/monitoring/alerts/rules/${editingRule.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingRule),
        });
        if (!res.ok) throw new Error('Failed to update');
        toast.success('Updated', 'Alert rule updated');
      } else {
        const res = await apiFetch('/api/admin/monitoring/alerts/rules', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingRule),
        });
        if (!res.ok) throw new Error('Failed to create');
        toast.success('Created', 'Alert rule created');
      }
      setEditingRule(null);
      await loadAlertRules();
    } catch (err) { toast.error('Error', err.message); }
    finally { setSavingRule(false); }
  };

  const handleDeleteRule = async () => {
    if (!deleteRuleConfirm) return;
    try {
      const res = await apiFetch(`/api/admin/monitoring/alerts/rules/${deleteRuleConfirm}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted', 'Alert rule removed'); await loadAlertRules(); }
    } catch { toast.error('Error', 'Failed to delete rule'); }
    finally { setDeleteRuleConfirm(null); }
  };

  const handleAcknowledge = async (id) => {
    try {
      const res = await apiFetch(`/api/admin/monitoring/alerts/${id}/acknowledge`, { method: 'POST' });
      if (res.ok) { toast.success('Done', 'Alert acknowledged'); await loadAlertHistory(); }
    } catch { toast.error('Error', 'Failed to acknowledge'); }
  };

  const ruleColumns = [
    { key: 'server_id', header: 'Server', render: row => {
      if (!row.server_id) return <Badge variant="info">All Servers</Badge>;
      const s = servers.find(s => s.id === row.server_id);
      return s ? s.name : `#${row.server_id}`;
    }},
    { key: 'metric', header: 'Metric', render: row => <Badge variant="pending">{row.metric.replace(/_/g, ' ')}</Badge> },
    { key: 'condition', header: 'Condition', render: row => `${row.operator} ${row.threshold}${row.metric.includes('usage') ? '%' : ''}` },
    { key: 'action', header: 'Action', render: row => row.action },
    { key: 'enabled', header: 'Status', render: row => <Badge variant={row.enabled ? 'active' : 'suspended'}>{row.enabled ? 'Enabled' : 'Disabled'}</Badge> },
    { key: 'actions', header: '', width: '120px', render: row => (
      <div className="admin-btn-group">
        <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => setEditingRule({ ...row })}>Edit</button>
        <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setDeleteRuleConfirm(row.id)}>Del</button>
      </div>
    )},
  ];

  const alertColumns = [
    { key: 'created_at', header: 'Time', width: '160px', render: row => new Date(row.created_at).toLocaleString() },
    { key: 'server_id', header: 'Server', render: row => {
      const s = servers.find(s => s.id === row.server_id);
      return s ? s.name : `#${row.server_id}`;
    }},
    { key: 'metric', header: 'Metric', render: row => <Badge variant="pending">{(row.metric || '').replace(/_/g, ' ')}</Badge> },
    { key: 'value', header: 'Value', render: row => `${row.value}${(row.metric || '').includes('usage') ? '%' : ''}` },
    { key: 'threshold', header: 'Threshold', render: row => `${row.threshold}${(row.metric || '').includes('usage') ? '%' : ''}` },
    { key: 'message', header: 'Message' },
    { key: 'acknowledged', header: 'Status', width: '100px', render: row => row.acknowledged ? <Badge variant="active">Ack</Badge> : (
      <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => handleAcknowledge(row.id)}>Ack</button>
    )},
  ];

  const chartData = history.map(h => ({
    time: new Date(h.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    cpu: h.cpu_usage,
    ram: h.ram_usage,
    disk: h.disk_usage,
  }));

  return (
    <div>
      <SectionHeader
        title="Server Monitoring"
        description="Real-time server health and alerting"
        actions={
          <div className="admin-btn-group">
            <button className="admin-btn admin-btn--primary" onClick={() => handleCheckNow()} disabled={checking}>
              {checking ? <><span className="admin-spinner" style={{ marginRight: '6px' }} />Checking...</> : <><i className="fa-solid fa-arrows-rotate" style={{ marginRight: '4px' }} />Check All</>}
            </button>
          </div>
        }
      />

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderBottom: '1px solid var(--admin-border)' }}>
        {['overview', 'alerts', 'history'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 16px', fontSize: '13px', fontWeight: 500, border: 'none', background: 'none', cursor: 'pointer',
            color: tab === t ? 'var(--admin-accent)' : 'var(--admin-text-secondary)',
            borderBottom: tab === t ? '2px solid var(--admin-accent)' : '2px solid transparent',
          }}>
            {t === 'overview' ? 'Server Overview' : t === 'alerts' ? 'Alert Rules' : 'Alert History'}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {[1,2].map(i => <div key={i} className="admin-card" style={{ height: '240px' }}><div className="admin-skeleton" style={{ width: '60%', height: '16px' }} /></div>)}
          </div>
        ) : servers.length === 0 ? (
          <div className="admin-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--admin-text-muted)' }}>
            <i className="fa-solid fa-server" style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }} />
            <p>No servers configured. Add servers in the Servers section first.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {servers.map(server => (
              <ServerCard key={server.id} server={server} onCheckNow={handleCheckNow} onViewHistory={handleViewHistory} />
            ))}
          </div>
        )
      )}

      {/* Alert Rules Tab */}
      {tab === 'alerts' && (
        <div className="admin-card">
          <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--admin-border)' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-text-primary)' }}>Alert Rules</span>
            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => setEditingRule({ server_id: null, metric: 'cpu_usage', operator: 'gt', threshold: 90, action: 'in_app', cooldown_minutes: 30, enabled: 1 })}>
              <i className="fa-solid fa-plus" /> Add Rule
            </button>
          </div>
          <DataTable columns={ruleColumns} data={alertRules} emptyMessage="No alert rules configured" emptyIcon="fa-solid fa-bell" />
        </div>
      )}

      {/* Alert History Tab */}
      {tab === 'history' && (
        <div className="admin-card">
          <DataTable columns={alertColumns} data={alertHistory} emptyMessage="No alerts triggered yet" emptyIcon="fa-solid fa-bell-slash" />
        </div>
      )}

      {/* History Modal */}
      <AdminModal
        open={!!historyServer}
        onClose={() => { setHistoryServer(null); setHistory([]); }}
        title={`Health History — ${historyServer?.name || ''}`}
        width="700px"
      >
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {[6, 24, 72, 168].map(h => (
            <button key={h} className={`admin-btn admin-btn--sm ${historyHours === h ? 'admin-btn--primary' : 'admin-btn--secondary'}`}
              onClick={() => { setHistoryHours(h); if (historyServer) handleViewHistory(historyServer); }}>
              {h < 24 ? `${h}h` : `${h/24}d`}
            </button>
          ))}
        </div>
        {historyLoading ? (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span className="admin-spinner" /></div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis dataKey="time" tick={{ fill: 'var(--admin-text-muted)', fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fill: 'var(--admin-text-muted)', fontSize: 10 }} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={{ background: 'var(--admin-card)', border: '1px solid var(--admin-border)', borderRadius: '8px', fontSize: '12px' }} />
              <Line type="monotone" dataKey="cpu" name="CPU" stroke="#1aad9f" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="ram" name="RAM" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="disk" name="Disk" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-text-muted)' }}>No data for this period</div>
        )}
      </AdminModal>

      {/* Alert Rule Edit Modal */}
      <AdminModal
        open={!!editingRule}
        onClose={() => setEditingRule(null)}
        title={editingRule?.id ? 'Edit Alert Rule' : 'Add Alert Rule'}
        footer={
          <>
            <button className="admin-btn admin-btn--secondary" onClick={() => setEditingRule(null)}>Cancel</button>
            <button className="admin-btn admin-btn--primary" onClick={handleSaveRule} disabled={savingRule}>
              {savingRule ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {editingRule && (
          <>
            <FormField label="Server">
              <select className="admin-select" value={editingRule.server_id || ''} onChange={e => setEditingRule(r => ({ ...r, server_id: e.target.value ? parseInt(e.target.value) : null }))}>
                <option value="">All Servers</option>
                {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Metric">
              <select className="admin-select" value={editingRule.metric} onChange={e => setEditingRule(r => ({ ...r, metric: e.target.value }))}>
                <option value="cpu_usage">CPU Usage</option>
                <option value="ram_usage">RAM Usage</option>
                <option value="disk_usage">Disk Usage</option>
                <option value="docker_crashed">Docker Crashed</option>
                <option value="unreachable">Unreachable</option>
              </select>
            </FormField>
            <FormField label="Condition">
              <div style={{ display: 'flex', gap: '8px' }}>
                <select className="admin-select" style={{ width: '100px' }} value={editingRule.operator} onChange={e => setEditingRule(r => ({ ...r, operator: e.target.value }))}>
                  <option value="gt">Greater than</option>
                  <option value="lt">Less than</option>
                  <option value="eq">Equals</option>
                </select>
                <input className="admin-input" type="number" value={editingRule.threshold} onChange={e => setEditingRule(r => ({ ...r, threshold: parseFloat(e.target.value) }))} style={{ width: '100px' }} />
                <span style={{ alignSelf: 'center', color: 'var(--admin-text-muted)', fontSize: '12px' }}>
                  {editingRule.metric.includes('usage') ? '%' : ''}
                </span>
              </div>
            </FormField>
            <FormField label="Action">
              <select className="admin-select" value={editingRule.action} onChange={e => setEditingRule(r => ({ ...r, action: e.target.value }))}>
                <option value="in_app">In-App Notification</option>
                <option value="discord">Discord Webhook</option>
                <option value="email">Email</option>
              </select>
            </FormField>
            <FormField label="Cooldown (minutes)" hint="Minimum time between alerts for same rule">
              <input className="admin-input" type="number" value={editingRule.cooldown_minutes} onChange={e => setEditingRule(r => ({ ...r, cooldown_minutes: parseInt(e.target.value) || 30 }))} />
            </FormField>
            {editingRule.id && (
              <FormField label="Enabled">
                <select className="admin-select" value={editingRule.enabled} onChange={e => setEditingRule(r => ({ ...r, enabled: parseInt(e.target.value) }))}>
                  <option value={1}>Enabled</option>
                  <option value={0}>Disabled</option>
                </select>
              </FormField>
            )}
          </>
        )}
      </AdminModal>

      <ConfirmDialog
        open={!!deleteRuleConfirm}
        onClose={() => setDeleteRuleConfirm(null)}
        onConfirm={handleDeleteRule}
        title="Delete Alert Rule"
        message="Are you sure? This cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}

export default AdminMonitoring;
